import _ from 'lodash';
import moment from 'moment';
import { CONTENT_MODEL_ATTRIBUTES, CustomPlugin } from '@/annotation/ckEditorPlugins/CKEditorPlugins.constants';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import { deserializeRange } from '@/datetime/dateTime.utilities';
import { INTERACTIVE_CONTENT_WORKSTEP_VERSION } from '@/reportEditor/report.constants';
import { formatCapsuleLabel } from '@/utilities/chartHelper.utilities';
import type { Editor } from 'ckeditor5';
import type { ContentCallbacks } from '@/annotation/ckEditorPlugins/CKEditorPlugins.constants';
import type { VisualizationData } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import type { ChartViewContentProperties } from '@/reportEditor/ChartViewContent.molecule';
import type { XyPlotContentProps } from '@/scatterPlot/XyPlotContent.organism';
import type { TableBuilderPropsOnlyProps } from '@/tableBuilder/TableBuilderPropsOnly.organism';
import type { ChartViewActions, ChartViewData } from '@/trend/chart.types';
import type { RangeExport } from '@/trendData/duration.store';

const MINIMAP_HEIGHT = 35;

export enum SubVisualization {
  SCATTER_PLOT = 'scatterPlot',
  DENSITY_PLOT = 'densityPlot',
}

/**
 * Used for interactive content in Calendar, Chain, or Capsule Mode to transform props that had been serialized into
 * the correct form for ChartViewContent
 * @param props
 */
export const transformTrendProps = (props: VisualizationContentProps): VisualizationContentProps => {
  const chartProps = props as ChartViewContentProperties;
  // Capsule items had their formatter function removed when they were serialized, so add them back
  _.forEach(chartProps.data.items, (item) => {
    if (item.conditionId && item.dataLabels) {
      item.dataLabels.formatter = formatCapsuleLabel;
    }
  });

  chartProps.configuration.isInTopic = true;
  (chartProps.data.sqDurationStoreData.displayRange as RangeExport) = deserializeRange(
    chartProps.data.serializedDateRange,
  );
  return chartProps;
};

/**
 * Used for interactive content in Calendar, Chain, or Capsule Mode to cleanse props prior to saving them to the
 * backend. Here, we can de-duplicate data to reduce our footprint as much as possible.
 * @param props
 */
export function cleanseTrendProps(props: { data: ChartViewData }): typeof props {
  const newProps = _.cloneDeep(props);
  _.forEach(newProps.data.items, (item) => {
    // Remove data.items.samples because it duplicates data.items.data. and isn't used
    item.samples = undefined;
  });
  return newProps;
}

type VisualizationContentProps = XyPlotContentProps | TableBuilderPropsOnlyProps | ChartViewContentProperties;
export const VisualizationContentPropsTransformer: {
  [key in Visualization]: (props: VisualizationContentProps) => VisualizationContentProps;
} = {
  [Visualization.XY_PLOT]: _.identity,
  [Visualization.TABLE]: _.identity,
  [Visualization.TREEMAP]: _.identity,
  [Visualization.TREND]: transformTrendProps,
  [Visualization.STATISTICS_SUMMARY]: _.identity,
  [Visualization.NONE]: _.identity,
  [Visualization.PLUGIN]: _.identity,
  [Visualization.VANTAGE]: _.identity,
};

export const VisualizationContentProps: { [key in Visualization]: any } = {
  [Visualization.XY_PLOT]: {
    canShowPlot: () => true,
    fetchScatterPlot: _.noop,
    clearPointerValues: _.noop,
    setSelectedRegion: _.noop,
    zoomToRegion: _.noop,
    createAxisControl: _.noop,
    setSelectors: _.noop,
    rangeEditingEnabled: false,
    autoUpdateDisabled: true,
  },
  [Visualization.TREEMAP]: {
    onClickNode: _.noop,
    rangeEditingEnabled: false,
  },
  [Visualization.TREND]: {
    actions: {
      clearPointerValues: _.noop,
      setPointerValues: _.noop,
      selectItems: _.noop,
      updateDisplayRangeTimes: _.noop,
      setYExtremes: _.noop,
      setSelectedRegion: _.noop,
      setCapsuleTimeOffsets: _.noop,
      findCapsuleItem: _.noop,
      setTrendCapsuleColor: _.noop,
    } as ChartViewActions,
  },
  [Visualization.STATISTICS_SUMMARY]: {
    // Statistics Summary doesn't need special props for content rendering
  },
  [Visualization.TABLE]: {
    displayMetricOnTrend: () => {},
    /** JSON blobs strip functions; grid still invokes this on row updates (e.g. treemap). */
    runComputeTreemap: _.noop,
  },
  [Visualization.NONE]: {},
  [Visualization.PLUGIN]: {},
  [Visualization.VANTAGE]: {},
};

/**
 * Sends the data for a component to the renderer. The renderer then serializes the data to JSON and stores it on
 * the backend for use as interactive content. Because it is serialized to JSON all functions are removed and an
 * error is thrown if there are any values that are not primitives (i.e. a moment object).
 * @param data - The data to store. Usually all the props of the component.
 * @param transformers - A map of keys and associated functions to transform the data. Useful for cleansing.
 */
export function provideVisualizationData(
  data: VisualizationData,
  transformers: Record<string, (value: any) => any> = {},
) {
  const cleansedData = _.chain(data)
    .omitBy(_.isFunction)
    .mapValues((value, key) => (transformers[key] ? transformers[key](value) : value))
    .value();
  cleansedData.version = INTERACTIVE_CONTENT_WORKSTEP_VERSION;
  window.seeqGetVisualizationData = () => Promise.resolve(cleansedData);
}

interface SerializedRange {
  start: number;
  end: number;
  duration: number;
}

export const serializeDisplayRange = ({ start, end, duration }: RangeExport | SerializedRange): SerializedRange => ({
  start: start.valueOf(),
  end: end.valueOf(),
  duration: moment.isDuration(duration) ? duration.asMilliseconds() : duration.valueOf(),
});

export function getPropertyForContent(
  key: string,
  propertyOverrides: Record<string, any>,
  properties: Record<string, any>,
): any {
  return propertyOverrides?.[key] ?? properties[key];
}

export function setContentPropertyOverrides(editor: Editor, contentId: string, newProps: any) {
  const contentCallbacks = (editor.config.get(CustomPlugin.Content) as any).contentCallbacks as ContentCallbacks;
  const model = contentCallbacks.getCurrentModel(contentId);

  editor.model.change((writer) =>
    writer.setAttribute(
      CONTENT_MODEL_ATTRIBUTES.PROPERTY_OVERRIDES,
      _.merge({}, model.getAttribute(CONTENT_MODEL_ATTRIBUTES.PROPERTY_OVERRIDES) ?? {}, newProps),
      model,
    ),
  );
}

export function clearContentPropertyOverrides(editor: Editor, contentId: string) {
  const contentCallbacks = (editor.config.get(CustomPlugin.Content) as any).contentCallbacks as ContentCallbacks;
  const model = contentCallbacks.getCurrentModel(contentId);
  if (model) {
    editor.model.change((writer) => writer.setAttribute(CONTENT_MODEL_ATTRIBUTES.PROPERTY_OVERRIDES, undefined, model));
  }
}

/**
 * Returns true if the user clicks on interactive content and the underlying component should handle the click
 * instead of the editor.
 * @param htmlElement - the element to check
 * @return true if the element is interactive content and should handle the click
 */
export function callIfInteractiveContentHandlesEvent(htmlElement: HTMLElement): boolean {
  if (
    htmlElement.nodeName === 'I' &&
    // Move column icon for ag-grid
    (htmlElement.classList.contains('fa-arrows') ||
      // Sort icon for ag-grid
      htmlElement.classList.contains('fa-sort-amount-asc') ||
      htmlElement.classList.contains('fa-sort-amount-desc') ||
      // Filter icon for ag-grid
      htmlElement.classList.contains('fa-filter'))
  ) {
    return true;
  }

  if (
    htmlElement.nodeName === 'SPAN' &&
    (htmlElement.classList.contains('ag-icon-tree-open') || htmlElement.classList.contains('ag-icon-tree-closed'))
  ) {
    return true;
  }

  // Column groups for ag-grid
  if (htmlElement.closest('span.ag-column-drop-cell')) {
    return true;
  }

  // Treemap nodes
  if (
    (htmlElement.nodeName === 'rect' && htmlElement.classList.contains('highcharts-point-treemap')) ||
    (htmlElement.closest('.highcharts-data-labels') && htmlElement.closest('.highcharts-container'))
  ) {
    return true;
  }

  return false;
}
